import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import {
  defaultItemStoreOptions,
  defaultItemStoreQuery,
  defaultStoreOptions
} from '@/utils/pinia/defaults'
import { useApiClient } from '@/composables/api-client'
import { createQuery, createUrl } from '@/utils/pinia/helpers'
import type { StoreQuery, ItemStoreSetupOptions } from './types'

const storeSetup = <
  K extends string,
  T extends { [key in K]: string | number }
>(
  defaultOptions: ItemStoreSetupOptions<T[K]>,
  defaultQuery: StoreQuery
) => {
  const data = ref<T | null>(null) as Ref<T | null>
  const query = ref<StoreQuery | null>(null)

  const fetchItem = async (id: T[K], requestQuery: StoreQuery = {}) => {
    const client = await useApiClient()
    const mergedQuery = Object.assign({}, defaultQuery, requestQuery)
    const queryToRequest = createQuery(defaultOptions, mergedQuery)
    const url = createUrl(defaultOptions.path, id, mergedQuery)
    const requestOptions = Object.assign({}, { query: queryToRequest })

    data.value = await client.get<T>(url, requestOptions, {
      cache: defaultOptions.cache
    })

    return data.value
  }

  const updateData = (updates: Partial<T> = {}) => {
    if (data.value) {
      data.value = Object.assign({}, data.value, updates)
    }
  }

  return { data, query, fetchItem, updateData }
}

export const createItemStoreWrapper = <
  K extends string,
  T extends { [key in K]: string | number }
>() => {
  return <E extends Record<string, any> = {}>(
    key: string,
    options: ItemStoreSetupOptions<T[K]>,
    setup?: (context: ReturnType<typeof storeSetup<K, T>>) => E
  ) => {
    const storeOptions = Object.assign(
      {},
      defaultStoreOptions,
      defaultItemStoreOptions,
      options
    )

    const defaultQuery = Object.assign({}, defaultItemStoreQuery, options.query)

    return defineStore(key, () => {
      const context = storeSetup<K, T>(storeOptions, defaultQuery)

      const extending = Object.assign({}, setup?.(context))

      return { ...context, ...extending }
    })
  }
}
